"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.meiosisSetup = void 0;
const simple_stream_1 = require("./simple-stream");
const util_1 = require("./util");
const mergerino_1 = __importDefault(require("mergerino"));
const assoc = (prop, value, target) => {
    target[prop] = value;
    return target;
};
const concatIfPresent = (target, source) => source ? target.concat(source) : target;
const assembleInitialState = (nestedComponents) => nestedComponents
    ? Object.keys(nestedComponents).reduce((result, key) => assoc(key, Object.assign({}, nestedComponents[key].initial, assembleInitialState(nestedComponents[key].nested)), result), {})
    : {};
const getInitialState = (app) => Object.assign({}, app.initial, assembleInitialState(app.nested));
const assembleView = (nestedComponents) => nestedComponents
    ? Object.keys(nestedComponents).reduce((result, key) => {
        const nestedApp = nestedComponents[key];
        if (nestedApp.view !== undefined) {
            const view = nestedApp.view;
            return assoc(key, {
                view: (cell, ...args) => view(cell.nest(key), ...args),
                nested: assembleView(nestedApp.nested)
            }, result);
        }
        return result;
    }, {})
    : {};
const getView = (app) => assembleView(app.nested);
const assembleServices = (nestedComponents, getCell = (cell) => cell, getState = (state) => state) => nestedComponents
    ? Object.keys(nestedComponents).reduce((result, key) => {
        var _a;
        const nextGetCell = (cell) => getCell(cell).nest(key);
        const nextGetState = (state) => getState(state)[key];
        const nestedApp = nestedComponents[key];
        return concatIfPresent(result, (_a = nestedApp.services) === null || _a === void 0 ? void 0 : _a.map((service) => ({
            onchange: (state) => (service.onchange ? service.onchange(nextGetState(state)) : state),
            run: (cell) => service.run(nextGetCell(cell))
        }))).concat(assembleServices(nestedApp.nested, nextGetCell, nextGetState));
    }, [])
    : [];
const getServices = (app) => concatIfPresent([], app.services).concat(assembleServices(app.nested));
const baseSetup = ({ stream, app }) => {
    if (!stream) {
        stream = simple_stream_1.simpleStream;
    }
    const safeApp = app || {};
    const initial = getInitialState(safeApp);
    const view = getView(safeApp);
    const createStream = typeof stream === 'function' ? stream : stream.stream;
    const scan = stream.scan;
    const update = createStream();
    const states = scan((state, patch) => (0, mergerino_1.default)(state, patch), initial, update);
    return {
        states,
        update,
        view
    };
};
const nestPatch = (patch, prop) => ({ [prop]: patch });
const nestUpdate = (parentUpdate, prop) => (patch) => parentUpdate(nestPatch(patch, prop));
const nestCell = (states, parentUpdate, components) => (prop) => {
    const nestedStates = states.map((state) => state[prop]);
    const getNestedState = () => states()[prop];
    const nestedUpdate = nestUpdate(parentUpdate, prop);
    const nestedComponents = (0, util_1.get)(components, [prop, 'nested']);
    return {
        states: nestedStates,
        state: getNestedState(),
        getState: getNestedState,
        update: nestedUpdate,
        nest: nestCell(nestedStates, nestedUpdate, nestedComponents),
        nested: nestedComponents
    };
};
/**
 * Helper to setup the Meiosis pattern with [Mergerino](https://github.com/fuzetsu/mergerino).
 *
 * @template S the State type.
 *
 * @param config the Meiosis config for use with Mergerino
 *
 * @returns a stream of Meiosis cells.
 */
const meiosisSetup = (config) => {
    const stream = config === null || config === void 0 ? void 0 : config.stream;
    const app = config === null || config === void 0 ? void 0 : config.app;
    const { states, update, view } = baseSetup({
        stream,
        app
    });
    const nest = nestCell(states, update, view);
    const getState = () => states();
    const getCell = (state) => ({ states, state, getState, update, nest, nested: view });
    const dropRepeats = (0, simple_stream_1.createDropRepeats)(stream);
    if (app) {
        getServices(app).forEach((service) => {
            dropRepeats(states, service.onchange).map((state) => service.run(getCell(state)));
        });
    }
    const cells = dropRepeats(states).map(getCell);
    return cells;
};
exports.meiosisSetup = meiosisSetup;
